import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import commonStyles from "../common-style";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import CodeField from "../../components/CodeField";
import Loading from "../../components/Loading";
import MarkdownView from "../../components/MarkdownView";
import Text from "../../components/Text";
import { GET_DEFAULT_PROFILE_TYPE } from "../../graphql/identity";
import { useLazyQuery } from "@apollo/client";
import { StyleSheet, TextInput } from "react-native";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { ProfileType } from "../../__generated__/graphql";
import { useRecoilValue } from "recoil";
import { phoneNumberConfirmResult } from "../../atoms/auth";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { FirebaseManagerContext } from "../../contexts/FirebaseManager";
import { useTracking } from "../../hooks/tracking";

export const CELL_COUNT = 6;

type MessageInfo = {
    text: string;
    color?: string;
};

type ConfirmScreenProps = StackScreenProps<SignupSigninStackParamList, "ConfirmCode">;

const ConfirmationScreen = ({ navigation, route }: ConfirmScreenProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { trackEvent } = useTracking();
    const { phoneNumber, isSignup = false } = route.params;
    const [message, setMessage] = useState<MessageInfo>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [value, setValue] = useState("");
    const { user } = useContext(AuthManagerContext);
    const phoneNumberConfirmResultValue = useRecoilValue(phoneNumberConfirmResult);
    const { user: firebaseUser } = useContext(FirebaseManagerContext);

    const [getDefaultProfileType, { loading: getDefaultProfileTypeLoading }] =
        useLazyQuery(GET_DEFAULT_PROFILE_TYPE);

    const isValid = useMemo(() => {
        return !!phoneNumberConfirmResultValue && value.length >= CELL_COUNT;
    }, [value, phoneNumberConfirmResultValue]);

    const isLoading = isProcessing || getDefaultProfileTypeLoading;

    useEffect(() => {
        void trackEvent("ask_activation_code_showed");
    }, [trackEvent, isSignup]);

    const validate = useCallback(async () => {
        try {
            setMessage(undefined);
            setIsProcessing(true);

            await phoneNumberConfirmResultValue?.confirm(value);
            // signin will change the auth user
            // if user exists and is a worker the root navigations controller that will transition to the User stack
            // in all other cases, a hook will do the navigation to next screen
        } catch (err) {
            setMessage({
                text: i18n.t("screens.auth.confirmation_code.wrong_code"),
                color: "red",
            });
            setIsProcessing(false);
        }
    }, [setIsProcessing, phoneNumberConfirmResultValue, value, i18n]);

    useEffect(() => {
        // once user === null it means we have login to firebase (user has entered its code) and don't have an account yet
        if (firebaseUser && user === null) {
            void trackEvent("signup_started");
            void (async () => {
                const { data: { getDefaultProfileType: profileType } = {} } =
                    await getDefaultProfileType();

                if (profileType === ProfileType.Client) {
                    navigation.replace("CheckClientInfo");
                } else if (profileType === ProfileType.Worker) {
                    navigation.replace("CheckWorkerInfo");
                } else {
                    navigation.replace("SelectRole", { isSignup });
                }
            })();
        }
    }, [firebaseUser, user, navigation, getDefaultProfileType, isSignup, trackEvent]);

    const ref = useRef<TextInput>(null);

    useEffect(() => {
        if (isValid) {
            void validate();
        }
    }, [isValid, validate]);

    return (
        <VStack gap={1} v={0} h="2/3">
            <MarkdownView
                markdown={i18n.t("screens.auth.confirmation_code.description", {
                    phoneNumber,
                })}
            />
            <CodeField
                ref={ref}
                value={value}
                setValue={setValue}
                cellCount={6}
                autoFocus
                cellStyle={isLoading ? { backgroundColor: colors.backdrop } : {}}
            />
            {isLoading && <Loading />}
            <Text
                style={[
                    commonStyles.screenErrorMessage,
                    { color: message ? message.color : "transparent" },
                ]}
            >
                {message?.text}
            </Text>
        </VStack>
    );
};

export default ConfirmationScreen;
